import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, CalendarHeart } from "lucide-react";
import { ASSETS, tel } from "@/data/site";
import { useContent } from "@/context/ContentContext";
import { useOpenStatus } from "@/hooks/useOpenStatus";

function StatusPill({ status, className = "", testId = "header-status-pill" }) {
  return (
    <Link to="/contact" data-testid={testId} title={status.detail} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 font-sans text-[0.7rem] font-semibold tracking-wide transition-colors ${status.open ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-soft-border bg-cream text-taupe hover:bg-blush"} ${className}`}>
      <span className="relative inline-flex h-2 w-2">
        {status.open && <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400/70" />}
        <span className={`relative h-2 w-2 rounded-full ${status.open ? "bg-emerald-500" : "bg-taupe/50"}`} />
      </span>
      {status.short}
    </Link>
  );
}

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/pregnancy-care", label: "Pregnancy Care" },
  { to: "/womens-health", label: "Women's Health" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const CLINIC = useContent();
  const status = useOpenStatus();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled ? "bg-cream/90 py-2 shadow-soft backdrop-blur-md" : "bg-cream/40 py-4 backdrop-blur-sm"
      }`}
    >
      <div className="container-clinic flex items-center justify-between">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3">
          <img src={ASSETS.logo} alt={`${CLINIC.clinicName} logo`} className="h-12 w-12 rounded-full object-contain md:h-14 md:w-14" />
          <span className="hidden flex-col leading-tight sm:flex">
            <span className="font-serif text-lg text-charcoal md:text-xl">Dr. Mithee's</span>
            <span className="text-[0.6rem] font-semibold uppercase tracking-[0.25em] text-terracotta">Clinic for Women</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={({ isActive }) =>
                `link-underline font-sans text-sm tracking-wide transition-colors ${
                  isActive ? "text-terracotta" : "text-charcoal/80 hover:text-terracotta"
                }`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <StatusPill status={status} className="hidden xl:inline-flex" />
          <Link
            to="/appointment"
            data-testid="header-book-btn"
            className="hidden items-center gap-2 rounded-full bg-terracotta px-6 py-3 font-sans text-sm font-semibold text-cream shadow-soft transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-terracotta-dark active:scale-95 md:inline-flex"
          >
            <CalendarHeart className="h-4 w-4" /> Book an Appointment
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((o) => !o)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-soft-border bg-cream text-charcoal lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 top-[64px] z-40 origin-top bg-cream transition-[opacity,transform] duration-400 lg:hidden ${
          open ? "pointer-events-auto opacity-100" : "pointer-events-none -translate-y-2 opacity-0"
        }`}
      >
        <nav className="container-clinic flex flex-col gap-1 py-6" aria-label="Mobile">
          <StatusPill status={status} className="mb-3 w-fit" testId="mobile-status-pill" />
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "")}`}
              className={({ isActive }) =>
                `border-b border-soft-border py-4 font-serif text-2xl ${isActive ? "text-terracotta" : "text-charcoal"}`
              }
              end={n.to === "/"}
            >
              {n.label}
            </NavLink>
          ))}
          <Link
            to="/appointment"
            data-testid="mobile-book-btn"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-4 font-sans font-semibold text-cream"
          >
            <CalendarHeart className="h-5 w-5" /> Book an Appointment
          </Link>
          <a
            href={tel(CLINIC.appointmentMobile)}
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-terracotta px-6 py-4 font-sans font-semibold text-terracotta"
          >
            <Phone className="h-5 w-5" /> Call the Clinic
          </a>
        </nav>
      </div>
    </header>
  );
}
